/**
 * Compiled-module registry (runtime, player-safe). Compiled .uix/.uic modules are CJS-wrapped
 * (same convention as behaviours' loadBehaviour): evaluated via `new Function` with a require
 * shim providing 'vue' and '@engine/ui'. `.uic` imports compile to `__uic('<guid>')` lookups,
 * so cross-file imports need no bundler graph — only deps-first load order (deps come from the
 * module's own `__deps` export, recorded at import time).
 */
import type { Component } from '@vue/runtime-core';
import type { StyleSheetData } from '../types';

export interface UICompiledModule {
  guid: string;
  component: Component;
  styles: StyleSheetData[];
  /** uic guids this module imports (deps-first load order). */
  deps: string[];
}

const registry = new Map<string, UICompiledModule>();

export function registerUIModule(mod: UICompiledModule): void {
  registry.set(mod.guid, mod);
}

export function unregisterUIModule(guid: string): void {
  registry.delete(guid);
}

export function clearUIModules(): void {
  registry.clear();
}

export function getUIModule(guid: string): UICompiledModule | null {
  return registry.get(guid) ?? null;
}

/** Called by compiled modules at eval time (`const X = __uic('<guid>')`). */
export function __uic(guid: string): Component {
  const mod = registry.get(guid);
  if (!mod) {
    throw new Error(`[ui] component module '${guid}' is not registered — load .uic deps before dependents`);
  }
  return mod.component;
}

/** All stylesheets for a root module, deps-first (so the root's own rules win cascade ties). */
export function collectUIStyles(rootGuid: string): StyleSheetData[] {
  const seen = new Set<string>();
  const out: StyleSheetData[] = [];
  const visit = (guid: string): void => {
    if (seen.has(guid)) return;
    seen.add(guid);
    const mod = registry.get(guid);
    if (!mod) return;
    for (const dep of mod.deps) visit(dep);
    out.push(...mod.styles);
  };
  visit(rootGuid);
  return out;
}

interface UIModuleExports {
  default?: Component;
  __styles?: StyleSheetData[];
  __deps?: string[];
  [key: string]: unknown;
}

/** The `console` binding UI module code sees. Hosts back it with the engine Logger so
 *  `console.log` in a .uix/.uic script reaches the editor console / get_logs — same
 *  visibility contract as behaviours' `ctx.log` (raw global console only hits devtools). */
export interface UIConsole {
  log(...args: unknown[]): void;
  info(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
  debug(...args: unknown[]): void;
}

export interface UIModuleEnv {
  /** Require-shim module map ('vue', '@engine/ui'). */
  modules: Record<string, unknown>;
  console: UIConsole;
}

/** Build the sandbox env compiled UI modules run in. Pass your vue module (runtime-core
 *  suffices); pass a Logger-backed console or module logs stay devtools-only. */
export function createUIModuleEnv(vueModule: unknown, console?: UIConsole): UIModuleEnv {
  return {
    modules: { vue: vueModule, '@engine/ui': { __uic } },
    console: console ?? globalThis.console,
  };
}

/**
 * Evaluate a compiled (CJS-transformed) UI module and register it. Deps must already be
 * registered — `__uic` lookups run at eval time.
 */
export function loadUIModule(guid: string, jsSource: string, env: UIModuleEnv): UICompiledModule {
  const exports: UIModuleExports = {};
  const moduleObj = { exports };
  const requireShim = (name: string): unknown => {
    if (Object.prototype.hasOwnProperty.call(env.modules, name)) return env.modules[name];
    throw new Error(`[ui] module '${name}' is not provided to the UI module sandbox`);
  };
  // 'console' is a Function PARAMETER so bare console.* in module code binds to the env's
  // sink, not the devtools-only global.
  // eslint-disable-next-line no-new-func
  const factory = new Function('require', 'exports', 'module', 'console', `"use strict";\n${jsSource}\n`);
  factory(requireShim, exports, moduleObj, env.console);

  const exported = (moduleObj.exports ?? exports) as UIModuleExports;
  const component = exported.default;
  if (!component || (typeof component !== 'object' && typeof component !== 'function')) {
    throw new Error(`[ui] compiled module '${guid}' has no default component export`);
  }
  const mod: UICompiledModule = {
    guid,
    component,
    styles: exported.__styles ?? [],
    deps: exported.__deps ?? [],
  };
  registerUIModule(mod);
  return mod;
}
